import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { authedAtTimestampAtom } from '../../auth/state';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { useAdmissionFlow } from '../../hooks/useAdmissionFlow';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { useVisitorId } from '../useVisitorId';
import { admissionTrackingHistory } from '../waiting-room/admission/analytics/AdmissionTrackingHistory';

export function useClientEnterSessionRoomLogger() {
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const visitorUuid = useVisitorId();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId ?? '';
  const joinRoomMode = useAdmissionFlow();
  const videoPlatform = useVideoPlatform();
  const hasLoggedEnterRef = useRef(false);
  const { userViewed } = useTrackEvent();
  const getAuthedAtTimestamp = useAtomCallbackMemoized((get) => get(authedAtTimestampAtom), []);

  useEffect(
    function sendLoggingEventsOnPageLoad() {
      if (hasLoggedEnterRef.current) return;
      const resolvedPatientShortId = patientShortId ?? '';
      const resolvedProviderShortId = providerShortId ?? '';
      const resolvedAdmissionTransactionId = admissionTrackingHistory.getTransactionId() ?? '';
      sendLoggingEvents(TrackingEvents.CLIENT_ENTER_SESSION_ROOM, {
        visitorUuid,
        patientShortId: resolvedPatientShortId,
        providerShortId: resolvedProviderShortId,
        appointmentShortId,
        joinRoomMode,
        videoPlatform,
        admissionTransactionId: resolvedAdmissionTransactionId,
      });
      userViewed(EventTypeEnum.JOINED_SESSION, {
        patientShortId: resolvedPatientShortId,
        providerShortId: resolvedProviderShortId,
        appointmentShortId,
        source: videoPlatform ?? '',
        txId: resolvedAdmissionTransactionId,
        isAuthenticated: !!getAuthedAtTimestamp(),
      });
      hasLoggedEnterRef.current = true;
    },
    [
      appointmentShortId,
      getAuthedAtTimestamp,
      joinRoomMode,
      patientShortId,
      providerShortId,
      userViewed,
      videoPlatform,
      visitorUuid,
    ],
  );
}
